import type {
    CreateExtArgs,
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    ResourceLoaderArgs,
    ResourceLoaderDefinition
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {RMApi} from './rendererModel'
import type {Reference, SchemaCollection, VersionedSchemaCollectionId} from './csave/serverProtocolTypes'
import type {ClientSpecMap, EditorClientSpecMapEntry} from '@wix/document-services-types'
import {DATA_TYPES, RESOURCE_TYPES} from '../constants/constants'
import {getOverrideId} from '../utils/resourceLoaderUtils'
import {registerSchemaCollection} from '../utils/schemaUtils'
import type {ManifestUpdateMessage} from './distributor/messages'
import {ReportableError} from '@wix/document-manager-utils'
import type {AppComponentVersionsItem} from './documentServicesModel'

const INTERACTIONS = {
    LOAD_SCHEMAS: 'load-schemas',
    LOAD_VERSIONED_SCHEMAS: 'load-versioned-schemas',
    REGISTER_SCHEMAS: 'register-schemas',
    BATCH_REGISTER_IN_DM: 'batch-register-schemas-dm',
    UPDATE_SCHEMA_REFS_TO_VIEWER: 'update-schema-refs-to-viewer',
    SCHEMA_RESOURCES_LOADING_FAILED: 'schema-resources-loading-failed',
    VERSIONED_SCHEMA_RESOURCES_LOADING_FAILED: 'versioned-schema-resources-loading-failed'
}
export const MANIFEST_UPDATE_MESSAGE: ManifestUpdateMessage['messageType'] = 'MANIFEST_UPDATE'

export interface SchemaResourceLoaderExtensionAPI extends ExtensionAPI {
    schemaResourceLoader: {
        sendStoredReferencesToViewer(): void
        registerSchemaCollections(schemaCollections: SchemaCollection[]): void
    }
}

export interface SchemaCollectionServer {
    getSchemaCollectionsByAppIds(appDefinitionIds: string[], overrideIds?: string[]): Promise<SchemaCollection[]>
    batchGetVersionedSchemaCollections(versionedIds: VersionedSchemaCollectionId[]): Promise<SchemaCollection[]>
}

type TypeToReferencesMap = Record<string, Reference[]>
type NamespaceToReferencesMap = Record<string, TypeToReferencesMap>
export type SchemaCollectionReferencesMap = Record<string, NamespaceToReferencesMap>

const EVENTS = {
    SCHEMA_RESOURCE_LOADER: {
        DYNAMIC_REFERENCES_UPDATE_EVENT: 'DYNAMIC_REFERENCES_UPDATE_EVENT'
    }
}

const mergeReferencesIntoMap = (
    referencesMap: SchemaCollectionReferencesMap,
    appId: string,
    references: NamespaceToReferencesMap | undefined
) => {
    if (!references) return

    if (!referencesMap[appId]) {
        referencesMap[appId] = {}
    }

    for (const [namespace, typeToReferencesMap] of Object.entries(references)) {
        if (!_.isEmpty(typeToReferencesMap)) {
            if (!referencesMap[appId][namespace]) {
                referencesMap[appId][namespace] = {}
            }
            Object.assign(referencesMap[appId][namespace], typeToReferencesMap)
        }
    }
}

const NS_RENAMING_MAP: Record<string, string> = {
    context: 'contexts'
}

const NS_TO_REMOVE = ['functions']

const normalizeSchemaCollectionNamespaces = (schemaCollections: SchemaCollection[]) => {
    _.forEach(schemaCollections, schemaCollection => {
        _.forEach(NS_TO_REMOVE, ns => {
            if (schemaCollection?.namespaces?.[ns]) {
                delete schemaCollection.namespaces[ns]
            }
        })
        _.forEach(schemaCollection?.namespaces, (nsSchema, ns) => {
            if (schemaCollection?.namespaces && NS_RENAMING_MAP[ns]) {
                const newName = NS_RENAMING_MAP[ns]
                schemaCollection.namespaces[newName] = nsSchema
                delete schemaCollection.namespaces[ns]
            }
        })
    })
}

const extractVersionedSchemaCollectionIds = (
    appComponentVersions: Record<string, AppComponentVersionsItem>
): VersionedSchemaCollectionId[] =>
    _.flatMap(Object.values(appComponentVersions), item =>
        _.map(item.components, (component, schemaCollectionId) => ({
            schemaCollectionId,
            componentVersion: component.version
        }))
    )

export const createExtension = ({environmentContext, logger, dsConfig}: CreateExtensionArgument): Extension => {
    let storedReferences: SchemaCollectionReferencesMap = {}
    let viewerIsInitialized = false

    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const {dal, pointers, extensionAPI, coreConfig} = args
        const {schemaResourceLoader} = extensionAPI as SchemaResourceLoaderExtensionAPI
        const {rendererModel} = args.extensionAPI as RMApi

        const fetchSchemas = async (appDefIds: string[]): Promise<SchemaCollection[]> => {
            if (!appDefIds.length) {
                return []
            }

            const overrideIds: string[] = []
            for (const appDefId of appDefIds) {
                const overrideId = getOverrideId(dal, pointers, appDefId)
                if (overrideId) overrideIds.push(overrideId)
            }

            try {
                logger.interactionStarted(INTERACTIONS.LOAD_SCHEMAS)
                const schemaCollections: SchemaCollection[] =
                    await environmentContext.serverFacade.getSchemaCollectionsByAppIds(
                        appDefIds,
                        overrideIds.length > 0 ? overrideIds : undefined
                    )
                normalizeSchemaCollectionNamespaces(schemaCollections)
                logger.interactionEnded(INTERACTIONS.LOAD_SCHEMAS)
                return schemaCollections
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: INTERACTIONS.SCHEMA_RESOURCES_LOADING_FAILED,
                        message: `Resources loading failed for appIds: ${appDefIds}`,
                        tags: {appDefIds},
                        extras: {originalError: error}
                    })
                )
                throw error
            }
        }

        const fetchVersionedSchemas = async (appDefIds: string[]): Promise<SchemaCollection[]> => {
            if (!coreConfig.supportedFragments?.[RESOURCE_TYPES.APP_COMPONENT_VERSIONS]) {
                return []
            }
            const fragmentPointers = dal.getItemsInNamespace(RESOURCE_TYPES.APP_COMPONENT_VERSIONS, DATA_TYPES.data)
            if (!fragmentPointers.length) {
                return []
            }
            const appDefIdSet = new Set(appDefIds)
            const relevantVersions: Record<string, AppComponentVersionsItem> = {}
            for (const pointer of fragmentPointers) {
                if (appDefIdSet.has(pointer.id)) {
                    const item = dal.get(pointer) as AppComponentVersionsItem | undefined
                    if (item) {
                        relevantVersions[pointer.id] = item
                    }
                }
            }
            const versionedIds = extractVersionedSchemaCollectionIds(relevantVersions)
            if (!versionedIds.length) {
                return []
            }

            try {
                logger.interactionStarted(INTERACTIONS.LOAD_VERSIONED_SCHEMAS)
                const schemaCollections = await environmentContext.serverFacade.batchGetVersionedSchemaCollections(
                    versionedIds
                )
                normalizeSchemaCollectionNamespaces(schemaCollections)
                logger.interactionEnded(INTERACTIONS.LOAD_VERSIONED_SCHEMAS)
                return schemaCollections
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: INTERACTIONS.VERSIONED_SCHEMA_RESOURCES_LOADING_FAILED,
                        message: `Versioned schema loading failed for ${versionedIds.length} ids`,
                        extras: {originalError: error}
                    })
                )
                throw error
            }
        }

        // During migration, the same schemaCollectionId may appear in both the regular API
        // and the versioned API. In that case the
        // versioned collection takes precedence. Once all apps have fully migrated to the
        // versioned format this merge can be dropped.
        const mergeSchemaCollections = (
            regular: SchemaCollection[],
            versioned: SchemaCollection[]
        ): SchemaCollection[] => {
            const versionedIds = new Set(versioned.map(c => c.id).filter(Boolean))
            return [...regular.filter(c => !versionedIds.has(c.id)), ...versioned]
        }

        const load = async (appDefIds: string[]) => {
            const [regular, versioned] = await Promise.all([fetchSchemas(appDefIds), fetchVersionedSchemas(appDefIds)])
            const merged = mergeSchemaCollections(regular, versioned)
            if (merged.length) {
                logger.interactionStarted(INTERACTIONS.REGISTER_SCHEMAS)
                schemaResourceLoader.registerSchemaCollections(merged)
                logger.interactionEnded(INTERACTIONS.REGISTER_SCHEMAS)
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap
            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const getEditorAndHybridResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .filter(appSpec => {
                        const isEditorOrHybrid = ['Editor', 'Hybrid'].includes(
                            (appSpec as EditorClientSpecMapEntry).appType
                        )
                        const appDefId = (appSpec as EditorClientSpecMapEntry).appDefinitionId
                        const hasOverrideId = appDefId && !!getOverrideId(dal, pointers, appDefId)
                        return isEditorOrHybrid || hasOverrideId
                    })
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const loadAll = async () => {
            if (!rendererModel.isBuilderComponentModel()) {
                return
            }
            await load(getEditorAndHybridResources())
        }

        const initAvailableResources = async () => {}

        return [
            {
                resourceType: RESOURCE_TYPES.SCHEMA,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    const createExtensionAPI = ({eventEmitter, coreConfig}: CreateExtArgs): SchemaResourceLoaderExtensionAPI => {
        return {
            schemaResourceLoader: {
                sendStoredReferencesToViewer: () => {
                    if (!_.isEmpty(storedReferences)) {
                        eventEmitter.emit(
                            EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT,
                            storedReferences
                        )
                    }
                    viewerIsInitialized = true
                    storedReferences = {}
                },
                registerSchemaCollections: (schemaCollections: SchemaCollection[]) => {
                    const referencesMap: SchemaCollectionReferencesMap = {}
                    logger.interactionStarted(INTERACTIONS.BATCH_REGISTER_IN_DM)
                    coreConfig.schemaService.batchRegisterSchemas(() => {
                        for (const schemaCollection of schemaCollections) {
                            const {innerElements, appId} = schemaCollection
                            const topLevelComponentReferences = registerSchemaCollection(
                                dsConfig,
                                coreConfig.schemaService,
                                schemaCollection
                            )
                            mergeReferencesIntoMap(referencesMap, appId!, topLevelComponentReferences)
                            if (innerElements) {
                                for (const innerElement of innerElements) {
                                    const innerElementReferences = registerSchemaCollection(
                                        dsConfig,
                                        coreConfig.schemaService,
                                        innerElement
                                    )
                                    mergeReferencesIntoMap(referencesMap, appId!, innerElementReferences)
                                }
                            }
                        }
                    })
                    logger.interactionEnded(INTERACTIONS.BATCH_REGISTER_IN_DM)

                    if (!_.isEmpty(referencesMap)) {
                        if (viewerIsInitialized) {
                            logger.interactionStarted(INTERACTIONS.UPDATE_SCHEMA_REFS_TO_VIEWER)
                            eventEmitter.emit(
                                EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT,
                                referencesMap
                            )
                            logger.interactionEnded(INTERACTIONS.UPDATE_SCHEMA_REFS_TO_VIEWER)
                        } else if (coreConfig.withoutViewer) {
                            // no one needs references map
                        } else {
                            for (const [appId, namespaceRefs] of Object.entries(referencesMap)) {
                                mergeReferencesIntoMap(storedReferences, appId, namespaceRefs)
                            }
                        }
                    }
                }
            }
        }
    }

    return {
        name: 'schemaResourceLoader',
        dependencies: new Set(['schema', 'rendererModel']),
        createResourceLoader,
        createExtensionAPI,
        EVENTS
    }
}
